/**
 * Every string this surface renders.
 *
 * The copy addresses the operator, never the reviewer: no design rationale, no
 * technical vocabulary, and no restatement of a mechanic the screen already
 * performs. The lede names what arrives and states that nothing here leads
 * elsewhere — which is the one thing an operator cannot discover by looking.
 *
 * No digit appears as a literal in this file. Every count is interpolated from
 * the shared formatter at the render site, so the Arabic-Indic glyphs are
 * produced once, in one place, and the machine half stays ASCII.
 */

export const copy = {
  documentTitle: 'الإشعارات — وحدة تحكّم شاهدني',

  crumbs: {
    root: 'لوحة التشغيل',
    current: 'الإشعارات',
  },

  head: {
    title: 'الإشعارات',
    lede: 'ما وقع على حسابك: ردودُ الحالات التي أنت طرفٌ فيها، وأجوبةُ طلبات العناوين، وموافقاتُ التعليقات، وما يصدر من عناوين وحلقات. تُقرأ هنا ولا تقود إلى مكانٍ آخر.',
  },

  section: {
    heading: 'الصندوق',
    /** Visually hidden, and it names what the table partitions. */
    caption: 'إشعارات هذا الحساب، وتاريخ كلٍّ منها وحالة قراءته',
  },

  sort: {
    label: 'الفرز',
    options: {
      'date-desc': 'الأحدث أوّلاً',
      'date-asc': 'الأقدم أوّلاً',
      unread: 'غير المقروء أوّلاً',
    },
    announced: 'أُعيد ترتيب الصندوق.',
  },

  bulk: {
    /**
     * The disabled reason sits at the control, and the same line carries the
     * count — so one element answers «لماذا هذه مطفأة» and «كم اخترت».
     */
    idle: 'لم يُختَر شيء بعد. اختر صفوفاً لتفعيل أفعال الجملة.',
    /**
     * Split around its numeral rather than interpolated: the figure renders
     * inside `<bdi>`, because a count beside Arabic text is exactly the string
     * the bidirectional algorithm reorders.
     */
    selectedPrefix: 'اختير',
    selectedSuffix: 'من الصفوف.',
    markRead: 'علّمها مقروءة',
    markUnread: 'علّمها غير مقروءة',
    delete: 'احذف المختار',
    markedRead: (count: string) => `عُلّم ${count} من الإشعارات مقروءة.`,
    markedUnread: (count: string) => `عُلّم ${count} من الإشعارات غير مقروءة.`,
    deleted: (count: string) => `حُذف ${count} من الإشعارات.`,
  },

  columns: {
    selectAll: 'اختر كلّ الصفوف المعروضة',
    selectRow: (headline: string) => `اختر «${headline}»`,
    message: 'الرسالة',
    date: 'التاريخ',
    state: 'الحالة',
  },

  state: {
    unread: 'غير مقروء',
    read: 'مقروء',
  },

  pager: {
    label: 'صفحات الإشعارات',
    /**
     * The row count agrees with what it counts. Arabic has a singular, a dual,
     * a paucal for three to ten and a different form beyond it, and one suffix
     * serves none of the other three — «١ إشعاراً معروضاً» is wrong in exactly
     * the place a last page of one row puts it. The singular and the dual carry
     * the quantity in the word, so no numeral is drawn beside them.
     */
    shownOne: 'إشعار واحد معروض',
    shownTwo: 'إشعاران معروضان',
    shownFew: 'إشعارات معروضة',
    shownMany: 'إشعاراً معروضاً',
  },

  states: {
    loading: 'جارٍ تحميل الإشعارات',
    /** Empty because nothing is waiting — the good state, and it offers no action. */
    empty: 'لا إشعارات في هذا الحساب.',
    emptyPage: 'لا إشعارات في هذه الصفحة.',
  },
} as const;
